/** @jsxImportSource @emotion/react */

import { FC, useMemo } from "react";
import CostsTable from "../components/CostsTable";
import {
  DocumentStages,
  DocumentStatus,
  Result,
  ResultStatus,
} from "../../../../graphql/__GENERATED__/types";
import DetailsSection from "../../../../components/Details/components/Main/Section/DetailsSection";
import Row from "../../../../components/Grid/Row";
import Column from "../../../../components/Grid/Column";
import Typography from "../../../../components/Typography";
import QuotationCardList from "../components/QuotationCardList";
import useDocumentDetails from "../../../../hooks/useDocumentDetails";
import { useUIStore } from "../../../../hooks/useUIStore";
import useConfirmedAction from "../../../../hooks/useConfirmedAction";
import { IconNames } from "../../../../components/Icons/styles/iconNames";
import useDocumentOrder from "../../../../hooks/useDocumentOrder";

const Costs: FC = () => {
  const {
    selectedResultId,
    setSelectedResultId,
    documentData: { selectedSimulationResults, status, stage },
  } = useDocumentDetails();

  const { handleApproveResult, handleChangeDocumentStage } =
    useDocumentDetails();
  const { isDrawerFullWidth } = useUIStore();

  const approvedResult = selectedSimulationResults?.filter(
    (result) => result?.status === ResultStatus.Approved
  );

  const selectedResult = useMemo(() => {
    return selectedSimulationResults?.find(
      (result) => result?._id === selectedResultId
    );
  }, [selectedResultId]);

  const resultToShow = useMemo(() => {
    if (stage === DocumentStages.Simulation) {
      return { result: selectedResult, resultId: [selectedResultId!] };
    } else {
      return {
        result: approvedResult?.[0],
        resultId: [approvedResult?.[0]?._id],
      };
    }
  }, [approvedResult, selectedResultId]);

  const handleClick = (resultId: string) => {
    stage === DocumentStages.Simulation && setSelectedResultId(resultId);
  };

  const onEditClick = useConfirmedAction(
    "Tem certeza? O documento retornará ao status de COTAÇÃO ENVIADA.",
    async () => handleChangeDocumentStage(DocumentStatus.Sent)
  );

  const currentStageStatus = useDocumentOrder(stage!, status!);
  const allowApproveOrReject = useDocumentOrder(
    DocumentStages.Simulation,
    DocumentStatus.Sent
  );
  const allowEdit = useDocumentOrder(DocumentStages.Policy);

  const footerButtons = useMemo(() => {
    if (currentStageStatus === allowApproveOrReject) {
      return [
        {
          text: "Recusar",
          onClick: () => {},
          iconName: IconNames.Rejected,
          disabled: !selectedResultId,
        },
        {
          text: "Aprovar",
          onClick: () => handleApproveResult(selectedResultId!),
          iconName: IconNames.Valid,
          disabled: !selectedResultId,
        },
      ];
    }
    if (currentStageStatus < allowEdit) {
      return [
        {
          text: "Editar",
          iconName: IconNames.Edit,
          onClick: onEditClick,
        },
      ];
    }
    return;
  }, [currentStageStatus, allowApproveOrReject, allowEdit, selectedResultId]);

  if (currentStageStatus < allowApproveOrReject) return <></>;

  return (
    <DetailsSection
      noTitle
      main
      title="Cotações enviadas"
      footerButtons={footerButtons}
    >
      {stage === DocumentStages.Simulation && (
        <Row>
          <Column>
            <Typography
              variant="textMedium"
              component="p"
              align="left"
              uppercase
            >
              Selecione uma das cotações abaixo:
            </Typography>
          </Column>
        </Row>
      )}
      <Row justify="start" align="center">
        <Column xs={12}>
          <QuotationCardList
            results={selectedSimulationResults as Result[]}
            activeCards={resultToShow.resultId as string[]}
            editing={stage === DocumentStages.Simulation}
            onClick={handleClick}
          />
        </Column>
      </Row>
      <Row>
        <Column xs={12} lg={isDrawerFullWidth ? 6 : 9}>
          {resultToShow.result && (
            <CostsTable selectedResult={resultToShow.result} />
          )}
        </Column>
      </Row>
    </DetailsSection>
  );
};

export default Costs;
